import React, { useState, useEffect } from "react";

export interface ReferenceBook {
  name: string;
  page: number;
  exerciseNumber: number;
}

export interface ReferenceTest {
  schoolName: string;
  year: number | string;
  session: string;
  class: string;
}

export interface ReferenceOther {
  info: string;
}

type ReferenceType = "livre" | "epreuve" | "other";

interface ReferencesFormProps {
  onSend: (referenceType: ReferenceType, data: any) => void;
  handleChange: (
    input: string,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  values: { [key: string]: any };
}

const ReferencesForm: React.FC<ReferencesFormProps> = ({
  onSend,
  handleChange,
  values,
}) => {
  const [reference, setReference] = useState<ReferenceType>(
    values.referenceType,
  );
  const [referenceBook, setReferenceBook] = useState<ReferenceBook>(
    values.referenceBook || {
      name: "",
      page: 0,
      exerciseNumber: 0,
    },
  );
  const [referenceTest, setReferenceTest] = useState<ReferenceTest>(
    values.referenceTest || {
      schoolName: "",
      year: "",
      session: "",
      class: "",
    },
  );
  const [referenceOther, setReferenceOther] = useState<ReferenceOther>(
    values.referenceOther || {
      info: "",
    },
  );

  useEffect(() => {
    // Mettre à jour les valeurs chaque fois que les props changent
    // setReference(values.referenceType);
    // setReferenceBook(values.referenceBook || { name: "", page: 0, exerciseNumber: 0 });
    // setReferenceTest(values.referenceTest || { schoolName: "", year: 0, session: "", class: "" });
    // setReferenceOther(values.referenceOther || { info: "" });
    switch (reference) {
      case "livre":
        onSend(reference, referenceBook);
        setReferenceTest({
          schoolName: "",
          year: "",
          session: "",
          class: "",
        });
        setReferenceOther({
          info: "",
        });
        break;
      case "epreuve":
        onSend(reference, referenceTest);
        setReferenceBook({
          name: "",
          page: 0,
          exerciseNumber: 0,
        });
        setReferenceOther({
          info: "",
        });
        break;
      case "other":
        onSend(reference, referenceOther);
        setReferenceTest({
          schoolName: "",
          year: "",
          session: "",
          class: "",
        });
        setReferenceBook({
          name: "",
          page: 0,
          exerciseNumber: 0,
        });
        break;
      default:
        console.error("Type de référence invalide");
    }
  }, [reference, referenceBook, referenceOther, referenceTest]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Envoyer les données au parent
    switch (reference) {
      case "livre":
        onSend(reference, referenceBook);
        break;
      case "epreuve":
        onSend(reference, referenceTest);
        break;
      case "other":
        onSend(reference, referenceOther);
        break;
      default:
        console.error("Type de référence invalide");
    }
  };

  const anneesScolaires = Array.from({ length: 30 }, (_, index) =>
    (new Date().getFullYear() - index).toString(),
  );

  return (
    <form
      onSubmit={(e) => handleSubmit(e)}
      className="max-w-md mx-auto p-8 bg-white rounded"
    >
      {/* <div className="flex flex-col items-center justify-center">
        <h2 className="font-bold text-2xl mb-1">Contrôle de Validation</h2>
        <p>Faites valider votre proposition</p>
      </div> */}

      <div className="flex mt-5 mb-4 items-center justify-center w-full">
        <label className="block mb-2 w-full">
          <select
            className="p-2 border border-gray-300 rounded w-full"
            value={reference}
            onChange={(e) => setReference(e.target.value as ReferenceType)}
          >
            <option value="" disabled>
              De quelle source provient votre exercice ?
            </option>
            <option value="livre">Livre</option>
            <option value="epreuve">Épreuve</option>
            <option value="other">Autre</option>
          </select>
        </label>
      </div>

      {reference === "livre" && (
        <>
          <div className="flex items-center justify-center mb-4 w-full">
            <label className="block mb-2 w-full">
              <input
                placeholder="Nom du livre"
                type="text"
                className="p-2 w-full border border-gray-300 rounded"
                value={referenceBook.name}
                onChange={(e) =>
                  setReferenceBook({ ...referenceBook, name: e.target.value })
                }
              />
            </label>
          </div>
          <div className="flex items-center justify-center mb-4 w-full">
            <label className="block mb-2 w-full">
              <input
                placeholder="Numéro de la page"
                type="number"
                min="1"
                className="p-2 w-full border border-gray-300 rounded"
                value={referenceBook.page || ""}
                onChange={(e) =>
                  setReferenceBook({
                    ...referenceBook,
                    page: parseInt(e.target.value) || 0,
                  })
                }
              />
            </label>
          </div>

          <div className="flex items-center justify-center mb-4 w-full">
            <label className="block mb-2 w-full">
              <input
                placeholder="Numéro de l'exercice"
                type="number"
                min="1"
                className="p-2 w-full border border-gray-300 rounded"
                value={referenceBook.exerciseNumber || ""}
                onChange={(e) =>
                  setReferenceBook({
                    ...referenceBook,
                    exerciseNumber: parseInt(e.target.value) || 0,
                  })
                }
              />
            </label>
          </div>
        </>
      )}

      {reference === "epreuve" && (
        <>
          <div className="flex items-center justify-center mb-4 w-full">
            <label className="block mb-2 w-full">
              <input
                type="text"
                placeholder="Nom de l'établissement"
                className="p-2 w-full border border-gray-300 rounded"
                value={referenceTest.schoolName}
                onChange={(e) =>
                  setReferenceTest({
                    ...referenceTest,
                    schoolName: e.target.value,
                  })
                }
              />
            </label>
          </div>

          <div className="flex items-center justify-center mb-4 w-full">
            <label className="block mb-2 w-full">
              <select
                placeholder="Année de l'épreuve"
                className="p-2 w-full border border-gray-300 rounded"
                value={referenceTest.year}
                onChange={(e) =>
                  setReferenceTest({
                    ...referenceTest,
                    year: e.target.value,
                  })
                }
              >
                <option value="" disabled selected>
                  Année scolaire
                </option>
                {anneesScolaires.map((annee) => (
                  <option key={annee} value={annee}>
                    {annee}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex items-center justify-center mb-4 w-full">
            <label className="block mb-2 w-full">
              <input
                type="text"
                placeholder="Session de l'épreuve"
                className="p-2 w-full border border-gray-300 rounded"
                value={referenceTest.session}
                onChange={(e) =>
                  setReferenceTest({
                    ...referenceTest,
                    session: e.target.value,
                  })
                }
              />
            </label>
          </div>

          <div className="flex items-center justify-center mb-4 w-full">
            <label className="block mb-2 w-full">
              <select
                className="p-2 border border-gray-300 rounded w-full"
                value={referenceTest.class}
                onChange={(e) =>
                  setReferenceTest({ ...referenceTest, class: e.target.value })
                }
              >
                <option value="" disabled selected>
                  Classe
                </option>
                <option value="terminale_A1">Terminale A1</option>
                <option value="terminale_A2">Terminale A2</option>
                <option value="terminale_B">Terminale B</option>
                <option value="terminale_C">Terminale C</option>
                <option value="terminale_D">Terminale D</option>
                <option value="terminale_G1">Terminale G1</option>
                <option value="terminale_G2">Terminale G2</option>
                <option value="terminale_G3">Terminale G3</option>
                <option value="troisieme_mc">Troisième MC</option>
                <option value="troisieme_ml">Troisième ML</option>
              </select>
            </label>
          </div>
        </>
      )}

      {reference === "other" && (
        <>
          <div className="flex items-center justify-center mb-4 w-full">
            <label className="block mb-2 w-full">
              Info:
              <textarea
                rows={4}
                className="p-2 border border-gray-300 rounded w-full"
                value={referenceOther.info}
                onChange={(e) => setReferenceOther({ info: e.target.value })}
              />
            </label>
          </div>
        </>
      )}
    </form>
  );
};

export default ReferencesForm;
